import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Lock,
  Menu as MenuIcon,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { LG_UP, SM_UP, useMediaQuery } from '@/lib/use-media-query';
import type { NavChild, NavGroup, NavItem } from '@/routes/nav/types';
import { FactitMonogram, FactitWordmark } from './factit-logo';
import { IconButton } from './icon-button';
import { Skeleton } from './skeleton';
import { Toaster } from './toast';
import { Tooltip } from './tooltip';
import { useToasterState } from './toast-store';

/*
 * Z-SCALE (spec R3(c); written down in docs/FRONTEND_COMPONENT_INVENTORY.md §5.5).
 * Every layer that floats uses one of these, and nothing in `features/` invents
 * its own number:
 *
 *   z-30  drawer overlay (the dimmed page behind the open drawer, below `lg`)
 *   z-40  drawer (the `aside` below `lg`)
 *   z-50  popovers, selects, comboboxes, dialogs and toasts — ALL portalled to
 *         `document.body`, so they escape the header's and the drawer's stacking
 *         contexts and sit above the drawer.
 *
 * Two layers at z-50 never coexist by accident: opening the drawer closes any
 * open popover. The drawer announces itself with a `appshell:drawer-open` event
 * on `document` (and `appshell:drawer-close` when it shuts); `TenantPicker`
 * listens to both. The literal names are repeated there on purpose: a feature
 * must not import a UI-kit module just to name an event.
 */

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.5. `NavItem` is NOT
 * redeclared here: it is the same type `src/routes/nav/nav.config.ts` builds
 * from the `<feature>.nav.ts` files, imported verbatim from
 * `src/routes/nav/types.ts`.
 */
export type { NavGroup, NavItem };

export interface AppShellProps {
  /**
   * The menu, in blocks. A single block with `label: null` is a flat menu.
   *
   * It went from `NavItem[]` to `NavGroup[]` after a usage report: ten
   * destinations all at the same weight and nobody knew which came first on day
   * one. The blocks are built by `navByGroup()`, which also resolves the lock —
   * the shell knows nothing of phases or onboarding, it only paints what it
   * receives.
   */
  nav: readonly NavGroup[];
  /** Identity strip. All three are nodes because the feature that knows about
   * session, tenant and environment builds them — the shell fetches nothing. */
  company: React.ReactNode;
  /** Mandatory, not optional: see the note on the decision further down. */
  environment: React.ReactNode;
  account: React.ReactNode;
  /** Header actions that must stay reachable at every width — the support chat
   * button. Unlike `account`, they do NOT move into the drawer below `sm`: they
   * sit in the header, right before the account block, and never shrink. */
  actions?: React.ReactNode;
  /** Sidebar collapsed to icons only. The user's preference: it is RECEIVED,
   * not stored and not persisted here. It applies from `lg` up only: below
   * `lg` the sidebar is a drawer, which always opens at full width with labels
   * (an icon-only drawer is a cramped, unlabelled menu). */
  collapsed?: boolean;
  /**
   * The menu is not known yet (the account is still resolving): the nav
   * landmark renders a few decorative skeleton rows instead of `nav`, so the
   * sidebar does not sit empty and jump when the real menu arrives.
   */
  navLoading?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** `prose` narrows the content width for forms and reading; `fluid` (the
   * default) lets it out to the app's maximum working width. */
  contentWidth?: 'fluid' | 'prose';
  children: React.ReactNode;
}

const MAIN_CONTENT_ID = 'app-shell-main-content';

/**
 * Base classes of a menu row (a first-level item, whether a `<Link>` or an
 * expanding `<button>`). `active` is the "I am on this screen or on one of its
 * children" state; `collapsed` centres the icon when there is no label.
 *
 * Below `sm` the menu is a drawer on a phone, and every row in it — first
 * level, second level, the block headers — is at least 44px tall
 * (`max-sm:min-h-11`), the touch size a finger needs; from `sm` up the rows
 * keep their compact height.
 */
const DRAWER_ROW_MIN_HEIGHT = 'max-sm:min-h-11';

const navRowVariants = cva(
  `flex w-full items-center gap-inset-sm rounded-md px-inset-sm py-2 text-body-s font-medium transition-colors focus-ring hover:bg-action-subtle ${DRAWER_ROW_MIN_HEIGHT}`,
  {
    variants: {
      active: {
        true: 'bg-action-subtle text-action',
        false: 'text-fg-muted',
      },
      collapsed: {
        true: 'justify-center',
        false: '',
      },
    },
    defaultVariants: {
      active: false,
      collapsed: false,
    },
  },
);

const navChildRowVariants = cva(
  // `flex` and not `block`: a child may carry a badge on the right (§4.12 asks
  // for one on «Conexiones» and «Entregas fallidas»), and with `block` the number
  // sat glued to the label instead of aligned to the edge.
  `flex items-center gap-stack-sm rounded-md px-inset-sm py-1.5 text-body-s transition-colors focus-ring hover:bg-action-subtle ${DRAWER_ROW_MIN_HEIGHT}`,
  {
    variants: {
      active: {
        true: 'font-medium text-action',
        false: 'text-fg-muted',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

/** `pathname` matches `to` itself or any of its child routes (`/dtes/123`
 * activates the `/dtes` destination). */
function isPathActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Key of the open/closed decision the user took per menu block, as a JSON
 * object `{ [groupId]: open }`. Only explicit clicks on a block's label are
 * written here.
 */
const BLOCK_DECISIONS_STORAGE_KEY = 'factit.appshell.blocks';

/**
 * `localStorage` can throw on access (Safari private browsing with storage
 * blocked), and what it holds can be anything a previous version wrote. A menu
 * preference is no reason for the shell not to mount, so every failure reads
 * as "no decision" and the blocks fall back to open.
 */
function readBlockDecisions(): ReadonlyMap<string, boolean> {
  try {
    const raw = window.localStorage.getItem(BLOCK_DECISIONS_STORAGE_KEY);
    if (raw === null) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return new Map();
    }
    return new Map(
      Object.entries(parsed).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
      ),
    );
  } catch {
    return new Map();
  }
}

function writeBlockDecision(id: string, open: boolean): void {
  try {
    const next = Object.fromEntries(readBlockDecisions());
    next[id] = open;
    window.localStorage.setItem(BLOCK_DECISIONS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // No persistence: the decision lasts as long as the tab does.
  }
}

/** Deterministic id of an item's submenu, for `aria-controls`. `useId` is not
 * used because the items are walked in a `.map()` and the rules of hooks do not
 * allow calling it in there. */
function submenuIdFor(to: string): string {
  return `app-shell-submenu-${to.replace(/[^a-zA-Z0-9]+/g, '-')}`;
}

/**
 * A destination that does not apply yet: SHOWN, with a lock and with the
 * reason. Hiding it was the obvious answer to "I did not know what I had to do"
 * and it is worse: an absent item teaches neither that it exists nor what
 * unlocks it.
 *
 * The reason lives only in a `Tooltip`, together with the full label the row
 * may truncate. It used to sit under every locked row as a caption, then under
 * the first one only; either way the darker caption outweighed the greyed
 * labels and the current step it was explaining (design critiques 2026-10).
 * The lock leads the row, so the state is read before the name.
 *
 * The row is focusable on purpose: a tooltip nobody can reach by keyboard or
 * touch is the `title` this codebase avoids. It is a `button` with
 * `aria-disabled`, so it is announced as unavailable and activating it only
 * reveals the reason, which is also its accessible description: a screen reader
 * hears it without opening anything.
 */
function LockedNavRow({
  label,
  reason,
  className,
  children,
}: {
  label: string;
  reason: string;
  className: string;
  /** The row's visible content, lock included; it must carry `label` as its text. */
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <li className="relative">
      <Tooltip
        side="right"
        description={reason}
        content={
          <>
            <span className="block font-medium">{label}</span>
            {reason}
          </>
        }
      >
        <button
          type="button"
          aria-disabled="true"
          // `w-full text-left`: a button shrinks to its content where a `div`
          // fills the row.
          className={cn(
            className,
            'w-full cursor-default text-left text-fg-disabled hover:bg-transparent',
          )}
        >
          {children}
        </button>
      </Tooltip>
    </li>
  );
}

/**
 * Where a second-level marker sits when the menu is expanded: in the gutter,
 * centred on the tree's line like a step on a stepper, so every label keeps the
 * same left edge whether it carries a marker or not. `bg-surface` masks the line
 * behind it. Collapsed, the submenu floats as a panel with no line and no
 * gutter, and the marker sits inline.
 */
const GUTTER_MARKER =
  'absolute left-[calc(-1*var(--spacing-inset-sm)-var(--spacing-icon-sm)/2)] top-1/2 -translate-y-1/2 bg-surface';

/** Read after the label by a screen reader: the marker itself is decorative. */
const PROGRESS_LABEL: Record<NonNullable<NavChild['progress']>, string> = {
  done: 'completado',
  current: 'paso actual',
};

/**
 * One second-level row: a lock when the step is not open yet, otherwise a link
 * with its progress marker (✓ behind, ● current) when the parent is a sequence.
 */
function NavChildRow({
  child,
  collapsed,
}: {
  child: NavChild;
  collapsed: boolean;
}): React.JSX.Element {
  if (child.blockReason !== undefined) {
    // A step that is not open yet: same treatment as a locked first-level
    // item (`LockedNavRow`).
    return (
      <LockedNavRow
        label={child.label}
        reason={child.blockReason}
        className={navChildRowVariants({ active: false })}
      >
        <Lock
          aria-hidden="true"
          className={cn('size-icon-sm shrink-0', !collapsed && GUTTER_MARKER)}
        />
        <span className="flex-1 truncate text-left" title={child.label}>
          {child.label}
        </span>
      </LockedNavRow>
    );
  }
  const marker =
    child.progress === 'done' ? (
      <Check
        aria-hidden="true"
        data-progress="done"
        className={cn('size-icon-sm shrink-0 text-success', !collapsed && GUTTER_MARKER)}
      />
    ) : child.progress === 'current' ? (
      <Circle
        aria-hidden="true"
        data-progress="current"
        className={cn(
          'size-icon-sm shrink-0 fill-current p-0.5 text-action',
          !collapsed && GUTTER_MARKER,
        )}
      />
    ) : null;
  return (
    <li className="relative">
      <NavLink
        to={child.to}
        className={({ isActive }) => cn(navChildRowVariants({ active: isActive }))}
      >
        {marker}
        {/* `title`: the steps' names are long («Declaración de cumplimiento de
            requisitos») and the column truncates them; the tooltip gives the
            full name back without changing the row height for everyone. */}
        <span className="flex-1 truncate text-left" title={child.label}>
          {child.label}
          {child.progress !== undefined ? (
            <span className="sr-only">{` (${PROGRESS_LABEL[child.progress]})`}</span>
          ) : null}
        </span>
        {/* `NavChild.badge` had been DECLARED since #150 and was never
            rendered: two of the three counters §4.12 asks for —
            «Conexiones» and «Entregas fallidas» — are children, so the tree
            could carry the number and the menu discarded it silently.
            Same markup as the parent's badge, on purpose: it is the same
            datum at another level, not another component. */}
        {typeof child.badge === 'number' ? (
          <span
            aria-label={`${child.badge} pendientes`}
            className="rounded-full bg-action-subtle px-1.5 text-caption font-semibold text-action"
          >
            {child.badge}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}

interface NavRowProps {
  item: NavItem;
  collapsed: boolean;
  expanded: boolean;
  onToggle: (to: string) => void;
  pathname: string;
}

function NavRow({
  item,
  collapsed,
  expanded,
  onToggle,
  pathname,
}: NavRowProps): React.JSX.Element {
  const Icon = item.icon;
  const hasChildren = Boolean(item.children && item.children.length > 0);
  const hasBadge = typeof item.badge === 'number';

  if (item.blockReason !== undefined) {
    return (
      <LockedNavRow
        label={item.label}
        reason={item.blockReason}
        className={navRowVariants({ collapsed })}
      >
        {/* The destination's icon stays — replacing it with the lock would erase
            which destination this is — and the lock is a badge on its corner.
            Between icon and label it pushed every locked label ~24px right of
            its open neighbours; as a badge the labels keep one left edge, and
            in icon mode the lock is still visible. */}
        <span className="relative shrink-0">
          <Icon aria-hidden="true" className="size-icon-md" />
          <Lock
            aria-hidden="true"
            className="absolute -bottom-1 -right-1 size-3 rounded-full bg-surface p-px"
          />
        </span>
        {collapsed ? (
          <span className="sr-only">{item.label}</span>
        ) : (
          <span className="flex-1 truncate text-left">{item.label}</span>
        )}
      </LockedNavRow>
    );
  }

  if (!hasChildren) {
    const content = (
      <>
        <Icon aria-hidden="true" className="size-icon-md shrink-0" />
        {!collapsed && (
          <span className="flex-1 truncate text-left">{item.label}</span>
        )}
        {!collapsed && hasBadge ? (
          <span
            aria-label={`${item.badge} pendientes`}
            className="rounded-full bg-action-subtle px-1.5 text-caption font-semibold text-action"
          >
            {item.badge}
          </span>
        ) : null}
      </>
    );

    // A destination that stands for a whole section (`matchPrefix`) stays lit
    // on every screen of it, not only on the one it opens. `NavLink` cannot do
    // that: it only puts `aria-current` on an exact (or descendant) match of
    // `to`, so the section's other screens render a plain `Link` marked by hand.
    if (
      item.matchPrefix !== undefined &&
      isPathActive(pathname, item.matchPrefix) &&
      !isPathActive(pathname, item.to)
    ) {
      return (
        <li>
          <Link
            to={item.to}
            aria-current="page"
            aria-label={collapsed ? item.label : undefined}
            title={collapsed ? item.label : undefined}
            className={cn(navRowVariants({ active: true, collapsed }))}
          >
            {content}
          </Link>
        </li>
      );
    }

    return (
      <li>
        <NavLink
          to={item.to}
          // Only the root needs `end`: without it, «/» would stay active on
          // every route of the shell, being a prefix of all of them.
          end={item.to === '/'}
          aria-label={collapsed ? item.label : undefined}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            cn(navRowVariants({ active: isActive, collapsed }))
          }
        >
          {content}
        </NavLink>
      </li>
    );
  }

  // An item WITH children does not navigate: it expands. If it navigated, the
  // first click would land on an arbitrary one of its children (the contract is
  // explicit about this — §5.5).
  const children = item.children ?? [];
  const childActive = children.some((child) =>
    isPathActive(pathname, child.to),
  );
  const submenuId = submenuIdFor(item.to);
  const overviews = children.filter((child) => child.overview !== undefined);
  const steps = children.filter((child) => child.overview === undefined);

  return (
    <li className="relative">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={submenuId}
        aria-label={collapsed ? item.label : undefined}
        title={collapsed ? item.label : undefined}
        className={cn(navRowVariants({ active: childActive, collapsed }))}
        onClick={() => onToggle(item.to)}
      >
        <Icon aria-hidden="true" className="size-icon-md shrink-0" />
        {!collapsed && (
          <span className="flex-1 truncate text-left">{item.label}</span>
        )}
        {!collapsed && hasBadge ? (
          <span
            aria-label={`${item.badge} pendientes`}
            className="rounded-full bg-action-subtle px-1.5 text-caption font-semibold text-action"
          >
            {item.badge}
          </span>
        ) : null}
        {!collapsed && (
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'size-icon-sm shrink-0 transition-transform',
              expanded && 'rotate-90',
            )}
          />
        )}
      </button>
      {expanded ? (
        <ul
          id={submenuId}
          className={cn(
            'flex flex-col gap-1',
            collapsed
              ? // Collapsed there is no width for the second level inline: it
                // floats as a panel beside the icon. It is still the same <ul>
                // in the document flow, so a keyboard user reaches the children
                // with Tab in the normal order.
                'absolute left-full top-0 z-50 ml-1 min-w-40 rounded-md border border-border bg-surface p-inset-sm shadow-overlay'
              : 'ml-6 mt-1',
          )}
        >
          {/* The overview sits above the tree's line, not on it: it is what the
              steps add up to, and on the line it read as step zero (user report,
              2026-11). Its icon takes the gutter the step markers use, so every
              label keeps one left edge. */}
          {overviews.map((child) => {
            const OverviewIcon = child.overview?.icon;
            return (
              // The padding stands in for the line's own: the link, not the
              // `li`, anchors the icon, so `GUTTER_MARKER` lands on the line's
              // axis exactly as it does for the steps.
              <li key={child.to} className={cn(!collapsed && 'pl-inset-sm')}>
                <NavLink
                  to={child.to}
                  end
                  className={({ isActive }) =>
                    cn(navChildRowVariants({ active: isActive }), 'relative font-medium')
                  }
                >
                  {OverviewIcon ? (
                    <OverviewIcon
                      aria-hidden="true"
                      className={cn('size-icon-sm shrink-0', !collapsed && GUTTER_MARKER)}
                    />
                  ) : null}
                  <span className="flex-1 truncate text-left">{child.label}</span>
                </NavLink>
              </li>
            );
          })}
          <li>
            <ul
              className={cn(
                'flex flex-col gap-1',
                !collapsed && 'border-l border-border pl-inset-sm',
              )}
            >
              {steps.map((child) => (
                <NavChildRow key={child.to} child={child} collapsed={collapsed} />
              ))}
            </ul>
          </li>
        </ul>
      ) : null}
    </li>
  );
}

/** How many placeholder rows `navLoading` shows: about a first screen of
 * destinations, not a guess at the real count. */
const NAV_SKELETON_ROWS = 5;

/** The destinations of ONE block. Extracted so it is not repeated between the
 *  sidebar's two modes (with labels and in icon mode). */
function NavList({
  items,
  collapsed,
  expanded,
  onToggle,
  pathname,
  ...ulProps
}: {
  items: readonly NavItem[];
  collapsed: boolean;
  expanded: ReadonlySet<string>;
  onToggle: (to: string) => void;
  pathname: string;
  // `onToggle` is omitted from the `<ul>`: the native one belongs to
  // `<details>` (`ToggleEvent`) and would clash with the item expander, which is
  // what this component forwards to `NavRow`.
} & Omit<React.ComponentPropsWithoutRef<'ul'>, 'onToggle'>): React.JSX.Element {
  return (
    <ul {...ulProps} className={cn('flex flex-col gap-1', ulProps.className)}>
      {items.map((item) => (
        <NavRow
          key={item.to}
          item={item}
          collapsed={collapsed}
          expanded={expanded.has(item.to)}
          onToggle={onToggle}
          pathname={pathname}
        />
      ))}
    </ul>
  );
}

/** `true` when the active route lives in this block, at the first or second
 * level. */
function groupContainsRoute(group: NavGroup, pathname: string): boolean {
  return group.items.some(
    (item) =>
      isPathActive(pathname, item.to) ||
      (item.matchPrefix !== undefined && isPathActive(pathname, item.matchPrefix)) ||
      (item.children ?? []).some((child) => isPathActive(pathname, child.to)),
  );
}

/**
 * Frame of the ~30 views with a session (component inventory §5.5).
 *
 * Contract decisions this component does NOT renegotiate:
 * - A SIDEBAR menu (256px expanded / 64px collapsed, icons only + tooltip), not
 *   a top bar: with ~10 first-level destinations and a real second level on
 *   three of them, a bar was already tight.
 * - `environment` is not optional: the product has sandbox, certificación and
 *   producción, and a consumed folio is not given back. It stays visible in the
 *   top strip at all times.
 * - `collapsed` is RECEIVED: it is the user's preference, and this component
 *   fetches nothing (neither the collapse, nor `nav`, nor the active company).
 * - The PRODUCT wordmark (`FactitWordmark`, or `FactitMonogram` when collapsed)
 *   lives here; TCIT's logo lives in `CenteredCardLayout`.
 *
 * It mounts the application's ONLY `<Toaster />` (§5.6). Exactly one, and here:
 * the `useToast()` hook publishes into a store outside React, so any screen can
 * report without wrapping anything, but with two `Toaster`s mounted the same
 * notice would render twice and be announced twice.
 */
/**
 * The glue between `useToast()`'s store and the presentational `Toaster`.
 *
 * It lives here and not in `toast.tsx` because `Toaster` is pure by contract —
 * it receives `toasts` and `onDismiss` — and the shell is the only place
 * guaranteed to have exactly one.
 */
function AppToaster(): React.JSX.Element {
  const { toasts, onDismiss } = useToasterState();
  return <Toaster toasts={toasts} onDismiss={onDismiss} />;
}

export function AppShell({
  nav,
  company,
  environment,
  account,
  actions,
  collapsed: collapsedPreference = false,
  navLoading = false,
  onCollapsedChange,
  contentWidth = 'fluid',
  children,
}: AppShellProps): React.JSX.Element {
  const location = useLocation();
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // What is stored is the user's EXPLICIT decision per block; with no decision
  // `collapsedInitially` rules (and the blocks `navByGroup` builds leave it
  // unset, so a block nobody touched is open).
  //
  // The decision persists across reloads (`readBlockDecisions`). It used to be
  // per tab, and «Operación» was collapsed by default while the company
  // certified: every reload — and the 404 — put DTEs one extra click away,
  // however many times the person had opened the block before. Now a block
  // stays the way its owner left it, and a route change never closes one.
  const [blockDecision, setBlockDecision] = React.useState<
    ReadonlyMap<string, boolean>
  >(readBlockDecisions);

  const isBlockOpen = React.useCallback(
    (group: NavGroup): boolean =>
      blockDecision.get(group.id) ?? group.collapsedInitially !== true,
    [blockDecision],
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // The drawer applies below `lg`; the default is the desktop answer, which is
  // what jsdom (no `matchMedia`) and the first server-less render get.
  const isDesktop = useMediaQuery(LG_UP, true);
  const isSmUp = useMediaQuery(SM_UP, true);
  // The icon-only mode is a desktop preference; the drawer below `lg` always
  // shows labels. The width follows the same rule in CSS (`lg:w-16`), so it
  // holds even before `matchMedia` answers.
  const collapsed = collapsedPreference && isDesktop;
  const asideId = React.useId();
  const hamburgerRef = React.useRef<HTMLButtonElement>(null);
  const drawerCloseRef = React.useRef<HTMLButtonElement>(null);
  const wasOpenRef = React.useRef(false);
  // `inert` takes the closed drawer out of the tab order and the accessibility
  // tree (its links would otherwise be tabbable while translated off-screen),
  // and takes the page behind an open drawer out of both. Nothing is inert at
  // `lg` and up, where the sidebar is an ordinary column.
  const drawerInert = !isDesktop && !mobileOpen;
  const pageInert = !isDesktop && mobileOpen;

  // When the active route is a child of an expandable item, that group starts
  // expanded: otherwise a keyboard user would never reach the `aria-current`
  // without first guessing that the group has to be opened by hand.
  React.useEffect(() => {
    const containing = nav
      .flatMap((group) => group.items)
      .find((item) =>
        item.children?.some((child) =>
          isPathActive(location.pathname, child.to),
        ),
      );
    if (containing) {
      setExpanded((prev) =>
        prev.has(containing.to) ? prev : new Set(prev).add(containing.to),
      );
    }
  }, [location.pathname, nav]);

  // And for the same reason, a closed block containing the active route opens:
  // nobody can be left on a screen the menu does not show. It is an effect and
  // not a computation so it can still be closed again by hand afterwards. The
  // opening is NOT persisted: it is the route's doing, not the person's, and
  // the next reload on another screen keeps the block the way they left it.
  React.useEffect(() => {
    const withActiveRoute = nav.find((group) =>
      groupContainsRoute(group, location.pathname),
    );
    if (!withActiveRoute) return;
    setBlockDecision((prev) => {
      const isOpen =
        prev.get(withActiveRoute.id) ??
        withActiveRoute.collapsedInitially !== true;
      // Already open: no decision is recorded, so the block keeps following the
      // default when the phase changes.
      return isOpen ? prev : new Map(prev).set(withActiveRoute.id, true);
    });
  }, [location.pathname, nav]);

  // Navigating closes the mobile drawer; on desktop this does nothing visible
  // because the drawer does not apply there (see the breakpoint further down).
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Opening: focus moves into the drawer (its close button is the first
  // focusable one at this width) and every floating layer is told to close.
  // Closing: focus goes back to the hamburger that opened it. The first render
  // is neither, so `wasOpenRef` keeps the page load from stealing focus.
  React.useEffect(() => {
    if (mobileOpen) {
      document.dispatchEvent(new CustomEvent('appshell:drawer-open'));
      drawerCloseRef.current?.focus();
    } else if (wasOpenRef.current) {
      document.dispatchEvent(new CustomEvent('appshell:drawer-close'));
      hamburgerRef.current?.focus();
    }
    wasOpenRef.current = mobileOpen;
  }, [mobileOpen]);

  // Escape closes the drawer from anywhere, not only from inside it — unless a
  // layer above it already handled that Escape. Radix layers (the organisation
  // popover inside the drawer, a Select, a Dialog) listen on `document` in the
  // capture phase and `preventDefault()` the Escape they dismiss on, so one
  // press closes only the topmost layer.
  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !event.defaultPrevented) setMobileOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  const toggleGroup = React.useCallback((id: string, open: boolean) => {
    setBlockDecision((prev) => new Map(prev).set(id, open));
    writeBlockDecision(id, open);
  }, []);

  const toggleExpanded = React.useCallback((to: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(to)) {
        next.delete(to);
      } else {
        next.add(to);
      }
      return next;
    });
  }, []);

  return (
    <div className="flex min-h-screen bg-page">
      {/* Skip link: the first tabbable element in the whole app. Without it, a
       * keyboard user walks the entire sidebar (~10 destinations plus their
       * second level) on every page load before reaching the real content. */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-inset focus:py-2 focus:text-body-s focus:font-medium focus:text-action focus-ring"
      >
        Saltar al contenido
      </a>

      {mobileOpen ? (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-30 bg-inverse opacity-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      {/* `lg:` is the system's desktop breakpoint (`--breakpoint-lg`, 1024px):
       * the width at which a navigation sidebar fits beside the content. Below
       * that the sidebar stops being a fixed column and becomes a drawer
       * (`fixed` + `translate`) over the content.
       *
       * Until `@tcit/tokens` 1.0.0-beta.7 this was Tailwind's stock value and
       * not a role: the tokens defined the grid's COLUMNS (`grid.columnas*`)
       * without the widths each one applies at. `theme.css` now resets
       * `--breakpoint-*` and redefines it from the token, so `lg:` here is the
       * same number but with provenance. */}
      <aside
        id={asideId}
        inert={drawerInert}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-surface shadow-modal transition-transform duration-200 ease-standard',
          // The icon-only width is desktop-only: below `lg` the drawer keeps
          // `w-64` whatever the preference.
          collapsedPreference && 'lg:w-16',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:z-auto lg:translate-x-0 lg:shadow-none',
        )}
      >
        {/* Expanded: the wordmark. Collapsed: the monogram, stacked over the
         * expand button, because 64px has no room for both side by side. */}
        <div
          className={cn(
            'flex items-center gap-inset-sm border-b border-border px-inset-sm',
            collapsed
              ? 'flex-col justify-center py-inset-sm'
              : 'h-row justify-between',
          )}
        >
          {collapsed ? (
            <FactitMonogram className="text-fg" />
          ) : (
            <FactitWordmark className="ml-inset-sm text-fg" />
          )}
          <div className="flex items-center gap-inset-sm">
            {/* Collapse/expand: only meaningful on desktop, where the sidebar
             * is a fixed column of variable width. `IconButton` carries the
             * hit-area contract (24px floor, 48px under a coarse pointer). */}
            <IconButton
              aria-pressed={collapsed}
              label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
              icon={collapsed ? PanelLeftOpen : PanelLeftClose}
              className="hidden lg:inline-flex"
              onClick={() => onCollapsedChange?.(!collapsed)}
            />
            {/* Close the drawer: only meaningful below the mobile breakpoint. */}
            <IconButton
              ref={drawerCloseRef}
              label="Cerrar menú"
              icon={X}
              className="lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
          </div>
        </div>

        {/* Below `sm` the header has no room for the account block, so it lives
         * here, under the drawer's header (spec R3(a), C-2). It is mounted in
         * ONE place at a time — here or in the header, never both — so the
         * organisation switcher and the logout button exist once. */}
        {!isSmUp ? (
          <div className="flex border-b border-border p-inset-sm">
            {account}
          </div>
        ) : null}

        <nav
          aria-label="Principal"
          className="flex-1 overflow-y-auto p-inset-sm"
        >
          {navLoading ? (
            <ul aria-hidden="true" className="flex flex-col gap-1">
              {Array.from({ length: NAV_SKELETON_ROWS }, (_, index) => (
                <li key={index} className="px-inset-sm py-2">
                  <Skeleton variant="text" />
                </li>
              ))}
            </ul>
          ) : null}
          {!navLoading && nav.map((group, index) => {
            const headerId = `app-shell-group-${group.id}`;
            const listId = `${headerId}-items`;

            // Icon mode: there is no width for labels, and a collapsible block
            // with no visible label would be a trap — nothing would say what is
            // inside or how to open it. So there are no blocks here: every
            // destination stays in view, separated by a line, and the block's
            // name remains available to screen readers.
            if (collapsed) {
              return (
                <NavList
                  key={group.id}
                  aria-label={group.label ?? undefined}
                  items={group.items}
                  collapsed={collapsed}
                  expanded={expanded}
                  onToggle={toggleExpanded}
                  pathname={location.pathname}
                  className={
                    index > 0
                      ? 'mt-inset-sm border-t border-border pt-inset-sm'
                      : undefined
                  }
                />
              );
            }

            // A block with no label: a flat menu. There is no collapse control
            // because there would be nowhere to put it.
            if (group.label === null) {
              return (
                <NavList
                  key={group.id}
                  items={group.items}
                  collapsed={collapsed}
                  expanded={expanded}
                  onToggle={toggleExpanded}
                  pathname={location.pathname}
                />
              );
            }

            const isOpen = isBlockOpen(group);

            return (
              <div
                key={group.id}
                className={index > 0 ? 'mt-inset' : undefined}
              >
                <button
                  id={headerId}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={listId}
                  className={cn(
                    'flex w-full items-center gap-inset-sm rounded-md px-inset-sm py-1.5 text-caption font-semibold uppercase tracking-wide text-fg-muted transition-colors hover:bg-action-subtle focus-ring',
                    DRAWER_ROW_MIN_HEIGHT,
                  )}
                  onClick={() => toggleGroup(group.id, !isOpen)}
                >
                  <span className="flex-1 truncate text-left">
                    {group.label}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'size-icon-sm shrink-0 transition-transform',
                      !isOpen && '-rotate-90',
                    )}
                  />
                </button>
                {isOpen ? (
                  <NavList
                    id={listId}
                    aria-labelledby={headerId}
                    items={group.items}
                    collapsed={collapsed}
                    expanded={expanded}
                    onToggle={toggleExpanded}
                    pathname={location.pathname}
                    className="mt-1"
                  />
                ) : null}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* `min-w-0` is THE line that stops the whole page from scrolling
       * sideways, and it is not interchangeable with the `min-w-0` on the
       *
       * header's children.
       *
       * This column is a child of a row flex (sidebar + content) and its
       * computed `min-width` is `auto`, that is, its CONTENT's MINIMUM width:
       * while the top strip did not fit, this column grew with it and dragged
       * the whole document along. Measured at 390px: `document.scrollWidth`
       * gave 620px with the old strip and 577px with the header's children
       * already giving — the children's `min-w-0` was not enough, because the
       * floor came from this node. What it looked like is every view cut off
       * on the right (the title, the description, each card's text), not just
       * the header. */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        {/* The strip is FIXED HEIGHT (`h-row`) and therefore has to be able
         * to shrink widthwise: if its children do not give, the header's
         * minimum width becomes the PAGE's minimum width and the browser puts
         * a horizontal scrollbar on the whole document. Measured on a 390px
         * iPhone: `scrollWidth` 620px, that is, 230px of content off screen —
         * it is what cut off the titles and descriptions of any view, not just
         * the header.
         *
         * The split: the left block takes whatever space is left
         * (`min-w-0 flex-1`) and inside it only the company gives; the rest is
         * `shrink-0`. And `gap`/`px` step down one on mobile — 20px of gutter
         * per gap, at 390px, is 10% of the usable width. */}
        <header
          inert={pageInert}
          className="flex h-row shrink-0 items-center gap-inset-sm border-b border-border bg-surface px-inset-sm sm:gap-inset sm:px-inset"
        >
          <div className="flex min-w-0 flex-1 items-center gap-inset-sm sm:gap-inset">
            <IconButton
              ref={hamburgerRef}
              label="Abrir menú"
              icon={MenuIcon}
              aria-expanded={mobileOpen}
              aria-controls={asideId}
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
            />
            {/* `environment` goes first and stays visible: it is the contract's
             * most important decision (§5.5) — not knowing which environment you
             * are in is how someone issues a real document believing they were
             * testing, and a consumed folio is not given back.
             *
             * Hence the `shrink-0`: "always visible" includes the phone, and it
             * is what decides WHAT gives when the width does not reach. */}
            <div className="shrink-0">{environment}</div>
            {/* The company is the only thing that gives: it shrinks and
             * truncates its label (the `truncate` on `Select`'s trigger) when
             * the width does not reach.
             *
             * `min-w-0` so it can shrink, and `max-w-popup-sm` so it does NOT
             * stretch: without the cap it took all the header's slack — 595px on
             * a 1280 monitor — and the chevron ended up 400px from the label it
             * governs, that is, too far to read as its affordance. The base
             * width still comes from the content (`flex` stays on its factory
             * `0 1 auto`), so a short name takes little. */}
            <div className="min-w-0 max-w-popup-sm">{company}</div>
          </div>
          {/* `min-w-0`, not `shrink-0`. The account block carries the
           * organisation switcher, whose width is a name someone chose — and
           * while this node refused to give, that name was a floor on the
           * header's minimum and therefore on the PAGE's: measured at 390px,
           * `document.scrollWidth` 591 with «Comercializadora e Inversiones del
           * Pacífico Sur SpA» signed in. It is the same mechanism the comment
           * above describes for the content column, one node to the right.
           *
           * What must NOT give is inside: the email caps and truncates itself,
           * the logout button keeps its own `shrink-0`, and below `sm` both
           * come down to an icon. The switcher is the one that gives. */}
          {actions ? (
            <div className="flex shrink-0 items-center gap-inset-sm">{actions}</div>
          ) : null}
          {isSmUp ? (
            <div className="flex min-w-0 items-center">{account}</div>
          ) : null}
        </header>

        <main
          id={MAIN_CONTENT_ID}
          inert={pageInert}
          tabIndex={-1}
          className={cn(
            // `p-inset` on mobile: 24px per side eats 48 of a phone's 390, and
            // what is lost is reading width for the content.
            'flex-1 p-inset focus:outline-none sm:p-inset-lg',
            contentWidth === 'prose'
              ? 'mx-auto w-full max-w-3xl'
              : 'mx-auto w-full max-w-6xl',
          )}
        >
          {children}
        </main>

        {/* Outside the <main> and outside the scrolling container: the Toaster
         * positions itself against the viewport, and an ancestor with an
         * `overflow` other than `visible` would clip it. */}
        <AppToaster />
      </div>
    </div>
  );
}
